import React, {useState, useEffect} from 'react';
import {
    Activity,
    Loader2,
    AlertTriangle,
    User,
    Mail,
    Search,
    Calendar,
    BarChart3,
    BarChart2,
    Clock,
    Sliders,
    Trash2,
    Edit3,
    RefreshCw,
    CheckCircle,
    XCircle,
    Info,
    Gauge,
    Zap,
    MessageSquare,
    Crown,
    ShieldAlert,
    Download,
    ArrowLeft,
    UserCheck,
    Undo2,
    ListChecks,
    PlusCircle,
    MinusCircle,
    RotateCcw,
    ChevronDown,
    ChevronUp,
    Sparkles
} from 'lucide-react';

const AdminUsagePage = () => {
    // User search and selection
    const [searchEmail, setSearchEmail] = useState('');
    const [selectedEmail, setSelectedEmail] = useState('');
    const [searchError, setSearchError] = useState(null);

    // Loading and error states
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // User usage data
    const [usageData, setUsageData] = useState(null);

    // Form states for different actions
    const [activeTab, setActiveTab] = useState('status'); // 'status', 'set-limits', 'set-counts', 'reset'
    const [limitsForm, setLimitsForm] = useState({
        dailyLimit: '',
        monthlyLimit: ''
    });
    const [countsForm, setCountsForm] = useState({
        dailyCount: '',
        monthlyCount: ''
    });
    const [resetForm, setResetForm] = useState({
        resetDaily: true,
        resetMonthly: false
    });

    // Calculate usage percentage for progress bars
    const calculatePercentage = (used, limit) => {
        if (!limit || limit === 0) return 0;
        const percentage = (used / limit) * 100;
        return Math.min(percentage, 100); // Cap at 100%
    };

    // Get status class based on usage percentage
    const getStatusClass = (percentage) => {
        if (percentage >= 90) return 'bg-red-500';
        if (percentage >= 70) return 'bg-orange-500';
        if (percentage >= 50) return 'bg-yellow-500';
        return 'bg-emerald-500';
    };

    // Format date from YYYY-MM-DD to more readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        const date = new Date(dateString);
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        };

        return date.toLocaleDateString(undefined, options);
    };

    // Show success message with timeout
    const showSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => {
            setSuccessMessage(null);
        }, 3000);
    };

    // Fetch user usage status
    const fetchUserStatus = async (email) => {
        setIsLoading(true);
        setError(null);
        setSearchError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/usage/status?email=${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    setSearchError('User not found');
                    setUsageData(null);
                    return;
                }
                throw new Error(`Failed to fetch user status: ${response.statusText}`);
            }

            const data = await response.json();
            setUsageData(data);
            setSelectedEmail(email);

            // Update form fields with current values
            setLimitsForm({
                dailyLimit: data.limits.daily || '',
                monthlyLimit: data.limits.monthly || ''
            });

            setCountsForm({
                dailyCount: data.used.daily || 0,
                monthlyCount: data.used.monthly || 0
            });

        } catch (error) {
            console.error('Error fetching user status:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Set custom limits for a user
    const setCustomLimits = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            // Build request body with only the fields that are provided
            const requestBody = {
                email: selectedEmail
            };

            if (limitsForm.dailyLimit !== '') {
                requestBody.daily_limit = parseInt(limitsForm.dailyLimit);
            }

            if (limitsForm.monthlyLimit !== '') {
                requestBody.monthly_limit = parseInt(limitsForm.monthlyLimit);
            }

            // Check if at least one limit is provided
            if (!('daily_limit' in requestBody) && !('monthly_limit' in requestBody)) {
                setError('Please provide at least one limit value');
                setIsLoading(false);
                return;
            }

            const response = await fetch(`https://api.digitalaalim.com/admin/usage/set-limits`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Failed to set limits: ${response.statusText}`);
            }

            const data = await response.json();
            showSuccess('Custom limits updated successfully');

            // Refresh user data
            await fetchUserStatus(selectedEmail);

        } catch (error) {
            console.error('Error setting custom limits:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Clear custom limits for a user
    const clearCustomLimits = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/usage/clear-limits`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: selectedEmail
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to clear limits: ${response.statusText}`);
            }

            const data = await response.json();
            showSuccess('Custom limits cleared successfully');

            // Refresh user data
            await fetchUserStatus(selectedEmail);

        } catch (error) {
            console.error('Error clearing custom limits:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Set usage counts for a user
    const setUsageCounts = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            // Build request body with only the fields that are provided
            const requestBody = {
                email: selectedEmail
            };

            if (countsForm.dailyCount !== '') {
                requestBody.daily_count = parseInt(countsForm.dailyCount);
            }

            if (countsForm.monthlyCount !== '') {
                requestBody.monthly_count = parseInt(countsForm.monthlyCount);
            }

            // Check if at least one count is provided
            if (!('daily_count' in requestBody) && !('monthly_count' in requestBody)) {
                setError('Please provide at least one count value');
                setIsLoading(false);
                return;
            }

            const response = await fetch(`https://api.digitalaalim.com/admin/usage/set-counts`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Failed to set counts: ${response.statusText}`);
            }

            const data = await response.json();
            showSuccess('Usage counts updated successfully');

            // Refresh user data
            await fetchUserStatus(selectedEmail);

        } catch (error) {
            console.error('Error setting usage counts:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Reset usage counters for a user
    const resetUsageCounters = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/usage/reset`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: selectedEmail,
                    reset_daily: resetForm.resetDaily,
                    reset_monthly: resetForm.resetMonthly
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to reset counters: ${response.statusText}`);
            }

            const data = await response.json();

            const resetTypes = [];
            if (resetForm.resetDaily) resetTypes.push('daily');
            if (resetForm.resetMonthly) resetTypes.push('monthly');

            showSuccess(`Successfully reset ${resetTypes.join(' and ')} counters`);

            // Refresh user data
            await fetchUserStatus(selectedEmail);

        } catch (error) {
            console.error('Error resetting counters:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle user search form submission
    const handleSearchSubmit = () => {
        if (!searchEmail.trim()) {
            setSearchError('Please enter an email address');
            return;
        }
        fetchUserStatus(searchEmail.trim());
    };

    // Custom UserInfoCard component for displaying user plan and details
    const UserInfoCard = ({user}) => {
        if (!user) return null;

        // Helper function to determine plan badge color
        const getPlanBadgeColor = (plan) => {
            switch (plan?.toLowerCase()) {
                case 'free':
                    return 'bg-gray-100 text-gray-800';
                case 'basic':
                    return 'bg-blue-100 text-blue-800';
                case 'premium':
                    return 'bg-purple-100 text-purple-800';
                case 'pro':
                    return 'bg-amber-100 text-amber-800';
                case 'researcher':
                    return 'bg-emerald-100 text-emerald-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        };

        return (
            <div className="bg-white rounded-xl shadow-sm p-6 mt-4 border border-gray-100">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                            <UserCheck className="h-6 w-6 text-indigo-600"/>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-medium text-gray-900">{user.email}</h3>
                                <span
                                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(user.plan)}`}>
                  {user.plan || 'No Plan'}
                </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                                <Calendar className="h-4 w-4 mr-1.5"/>
                                <span>Daily reset: {formatDate(user.resets.daily_on)}</span>
                                <span className="mx-2">•</span>
                                <Clock className="h-4 w-4 mr-1.5"/>
                                <span>Monthly reset: {user.resets.monthly_key}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2 self-end md:self-auto">
                        <button
                            onClick={() => fetchUserStatus(user.email)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1.5"/>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Usage Progress Meter Component
    const UsageProgressMeter = ({label, used, limit, icon}) => {
        const percentage = calculatePercentage(used, limit);
        const statusClass = getStatusClass(percentage);

        return (
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                    {icon}
                    <h3 className="text-base font-medium text-gray-800">{label}</h3>
                </div>

                <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-500">
                        {used} / {limit || '∞'}
                    </div>
                    <div className="text-sm font-medium text-gray-700">
                        {limit ? `${Math.round(percentage)}%` : 'No Limit'}
                    </div>
                </div>

                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full ${statusClass} transition-all duration-500 ease-in-out`}
                        style={{width: `${percentage}%`}}
                    ></div>
                </div>

                <div className="mt-3 text-sm text-gray-500">
                    {limit ? (
                        <>
                            <span className="font-medium text-gray-700">{Math.max(0, limit - used)}</span> remaining
                        </>
                    ) : (
                        <span className="italic">Unlimited</span>
                    )}
                </div>
            </div>
        );
    };

    // Action Tab Component
    const ActionTabButton = ({id, label, icon, isActive, onClick}) => (
        <button
            onClick={() => onClick(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
        >
            {icon}
            {label}
        </button>
    );

    return (
        <div className="space-y-8">
            {/* Header with subtle gradient background */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 shadow-sm">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Activity className="text-indigo-600" size={32}/>
                    Usage Control
                </h1>
                <p className="mt-2 text-gray-600">
                    Manage and adjust user query limits and usage counters
                </p>
            </div>

            {/* User Search Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Mail size={18} className="text-gray-400"/>
                        </div>
                        <input
                            type="email"
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                            placeholder="Enter user email address..."
                            className="pl-10 w-full border border-gray-300 rounded-md py-2 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            required
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearchSubmit();
                                }
                            }}
                        />
                    </div>
                    <button
                        onClick={handleSearchSubmit}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <Search size={18} className="mr-2"/>
                        Find User
                    </button>
                </div>

                {searchError && (
                    <div className="mt-3 text-sm text-red-600 flex items-center">
                        <AlertTriangle size={16} className="mr-1.5"/>
                        {searchError}
                    </div>
                )}
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-md animate-fadeInOut">
                    <div className="flex items-center">
                        <CheckCircle size={20} className="text-emerald-500 mr-3 flex-shrink-0"/>
                        <p className="text-emerald-700">{successMessage}</p>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                    <div className="flex items-center">
                        <AlertTriangle size={20} className="text-red-500 mr-3 flex-shrink-0"/>
                        <div>
                            <p className="text-red-700 font-medium">Error</p>
                            <p className="text-red-600">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="flex flex-col items-center">
                        <Loader2 size={40} className="animate-spin text-indigo-600 mb-4"/>
                        <p className="text-gray-500">Loading user data...</p>
                    </div>
                </div>
            ) : usageData ? (
                <div className="space-y-6">
                    {/* User Info Card */}
                    <UserInfoCard user={usageData}/>

                    {/* Usage Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <UsageProgressMeter
                            label="Daily Usage"
                            used={usageData.used.daily || 0}
                            limit={usageData.limits.daily}
                            icon={<BarChart2 size={18} className="text-blue-600"/>}
                        />
                        <UsageProgressMeter
                            label="Monthly Usage"
                            used={usageData.used.monthly || 0}
                            limit={usageData.limits.monthly}
                            icon={<BarChart3 size={18} className="text-purple-600"/>}
                        />
                    </div>

                    {/* Action Tabs */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                        <div className="border-b border-gray-200">
                            <div className="flex overflow-x-auto p-2 gap-1">
                                <ActionTabButton
                                    id="status"
                                    label="Status"
                                    icon={<Info size={16}/>}
                                    isActive={activeTab === 'status'}
                                    onClick={setActiveTab}
                                />
                                <ActionTabButton
                                    id="set-limits"
                                    label="Set Limits"
                                    icon={<Sliders size={16}/>}
                                    isActive={activeTab === 'set-limits'}
                                    onClick={setActiveTab}
                                />
                                <ActionTabButton
                                    id="set-counts"
                                    label="Adjust Counts"
                                    icon={<Edit3 size={16}/>}
                                    isActive={activeTab === 'set-counts'}
                                    onClick={setActiveTab}
                                />
                                <ActionTabButton
                                    id="reset"
                                    label="Reset Counters"
                                    icon={<RotateCcw size={16}/>}
                                    isActive={activeTab === 'reset'}
                                    onClick={setActiveTab}
                                />
                            </div>
                        </div>

                        <div className="p-6">
                            {/* Status Tab */}
                            {activeTab === 'status' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="flex items-center text-sm font-medium text-gray-500 mb-2">
                                                <Crown size={16} className="text-amber-500 mr-1.5"/>
                                                Plan
                                            </div>
                                            <p className="text-lg font-medium text-gray-900">{usageData.plan || 'No Plan'}</p>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="flex items-center text-sm font-medium text-gray-500 mb-2">
                                                <MessageSquare size={16} className="text-emerald-500 mr-1.5"/>
                                                Daily Limit
                                            </div>
                                            <p className="text-lg font-medium text-gray-900">
                                                {usageData.limits.daily || 'Unlimited'}
                                            </p>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="flex items-center text-sm font-medium text-gray-500 mb-2">
                                                <MessageSquare size={16} className="text-purple-500 mr-1.5"/>
                                                Monthly Limit
                                            </div>
                                            <p className="text-lg font-medium text-gray-900">
                                                {usageData.limits.monthly || 'Unlimited'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Current Usage Stats */}
                                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                            <h3 className="text-sm font-medium text-gray-700 flex items-center">
                                                <Gauge size={16} className="mr-1.5 text-indigo-600"/>
                                                Current Usage Statistics
                                            </h3>
                                        </div>
                                        <div className="divide-y divide-gray-200">
                                            <div className="grid grid-cols-3 py-3 px-4 text-sm">
                                                <div className="text-gray-500">Daily Used</div>
                                                <div
                                                    className="col-span-2 font-medium text-gray-900">{usageData.used.daily || 0}</div>
                                            </div>
                                            <div className="grid grid-cols-3 py-3 px-4 text-sm">
                                                <div className="text-gray-500">Daily Remaining</div>
                                                <div className="col-span-2 font-medium text-gray-900">
                                                    {usageData.limits.daily ? usageData.remaining.daily : 'Unlimited'}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 py-3 px-4 text-sm">
                                                <div className="text-gray-500">Monthly Used</div>
                                                <div
                                                    className="col-span-2 font-medium text-gray-900">{usageData.used.monthly || 0}</div>
                                            </div>
                                            <div className="grid grid-cols-3 py-3 px-4 text-sm">
                                                <div className="text-gray-500">Monthly Remaining</div>
                                                <div className="col-span-2 font-medium text-gray-900">
                                                    {usageData.limits.monthly ? usageData.remaining.monthly : 'Unlimited'}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 py-3 px-4 text-sm">
                                                <div className="text-gray-500">Daily Reset Date</div>
                                                <div
                                                    className="col-span-2 font-medium text-gray-900">{formatDate(usageData.resets.daily_on)}</div>
                                            </div>
                                            <div className="grid grid-cols-3 py-3 px-4 text-sm">
                                                <div className="text-gray-500">Monthly Reset</div>
                                                <div
                                                    className="col-span-2 font-medium text-gray-900">{usageData.resets.monthly_key}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Set Limits Tab */}
                            {activeTab === 'set-limits' && (
                                <div className="space-y-6">
                                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
                                        <div className="flex">
                                            <Info size={20} className="text-blue-500 mr-3 flex-shrink-0"/>
                                            <div>
                                                <p className="text-blue-700 font-medium">About Custom Limits</p>
                                                <p className="text-blue-600 text-sm">
                                                    Set custom limits to override the user's plan defaults. Set to 0 to
                                                    disable usage.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="dailyLimit"
                                                   className="block text-sm font-medium text-gray-700 mb-1">
                                                Custom Daily Limit
                                            </label>
                                            <div className="flex rounded-md shadow-sm">
                        <span
                            className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                          <MessageSquare size={16} className="text-gray-400"/>
                        </span>
                                                <input
                                                    type="number"
                                                    id="dailyLimit"
                                                    min="0"
                                                    value={limitsForm.dailyLimit}
                                                    onChange={(e) => setLimitsForm({
                                                        ...limitsForm,
                                                        dailyLimit: e.target.value
                                                    })}
                                                    placeholder="Enter custom daily limit"
                                                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500">
                                                Current plan default: {usageData.limits.daily || 'Unlimited'}
                                            </p>
                                        </div>

                                        <div>
                                            <label htmlFor="monthlyLimit"
                                                   className="block text-sm font-medium text-gray-700 mb-1">
                                                Custom Monthly Limit
                                            </label>
                                            <div className="flex rounded-md shadow-sm">
                        <span
                            className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                          <Calendar size={16} className="text-gray-400"/>
                        </span>
                                                <input
                                                    type="number"
                                                    id="monthlyLimit"
                                                    min="0"
                                                    value={limitsForm.monthlyLimit}
                                                    onChange={(e) => setLimitsForm({
                                                        ...limitsForm,
                                                        monthlyLimit: e.target.value
                                                    })}
                                                    placeholder="Enter custom monthly limit"
                                                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500">
                                                Current plan default: {usageData.limits.monthly || 'Unlimited'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={clearCustomLimits}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <Trash2 size={16} className="mr-2"/>
                                            Clear Custom Limits
                                        </button>
                                        <button
                                            type="button"
                                            onClick={setCustomLimits}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <Sliders size={16} className="mr-2"/>
                                            Apply Custom Limits
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Set Counts Tab */}
                            {activeTab === 'set-counts' && (
                                <div className="space-y-6">
                                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-md">
                                        <div className="flex">
                                            <Info size={20} className="text-amber-500 mr-3 flex-shrink-0"/>
                                            <div>
                                                <p className="text-amber-700 font-medium">Adjust Usage Counts</p>
                                                <p className="text-amber-600 text-sm">
                                                    Manually set current usage counters. Useful for decreasing usage or
                                                    granting extra quota.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="dailyCount"
                                                   className="block text-sm font-medium text-gray-700 mb-1">
                                                Daily Usage Count
                                            </label>
                                            <div className="flex rounded-md shadow-sm">
                        <span
                            className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                          <BarChart2 size={16} className="text-gray-400"/>
                        </span>
                                                <input
                                                    type="number"
                                                    id="dailyCount"
                                                    min="0"
                                                    value={countsForm.dailyCount}
                                                    onChange={(e) => setCountsForm({
                                                        ...countsForm,
                                                        dailyCount: e.target.value
                                                    })}
                                                    placeholder="Enter daily count"
                                                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                            <div className="mt-2 flex items-center space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({
                                                        ...countsForm,
                                                        dailyCount: Math.max(0, parseInt(countsForm.dailyCount || 0) - 1)
                                                    })}
                                                    className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    <MinusCircle size={16}/>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({
                                                        ...countsForm,
                                                        dailyCount: Math.max(0, parseInt(countsForm.dailyCount || 0) + 1)
                                                    })}
                                                    className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    <PlusCircle size={16}/>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({...countsForm, dailyCount: 0})}
                                                    className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    Reset to 0
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({
                                                        ...countsForm,
                                                        dailyCount: usageData.used.daily || 0
                                                    })}
                                                    className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    Restore
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="monthlyCount"
                                                   className="block text-sm font-medium text-gray-700 mb-1">
                                                Monthly Usage Count
                                            </label>
                                            <div className="flex rounded-md shadow-sm">
                        <span
                            className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                          <BarChart3 size={16} className="text-gray-400"/>
                        </span>
                                                <input
                                                    type="number"
                                                    id="monthlyCount"
                                                    min="0"
                                                    value={countsForm.monthlyCount}
                                                    onChange={(e) => setCountsForm({
                                                        ...countsForm,
                                                        monthlyCount: e.target.value
                                                    })}
                                                    placeholder="Enter monthly count"
                                                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                            <div className="mt-2 flex items-center space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({
                                                        ...countsForm,
                                                        monthlyCount: Math.max(0, parseInt(countsForm.monthlyCount || 0) - 1)
                                                    })}
                                                    className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    <MinusCircle size={16}/>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({
                                                        ...countsForm,
                                                        monthlyCount: Math.max(0, parseInt(countsForm.monthlyCount || 0) + 1)
                                                    })}
                                                    className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    <PlusCircle size={16}/>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({...countsForm, monthlyCount: 0})}
                                                    className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    Reset to 0
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCountsForm({
                                                        ...countsForm,
                                                        monthlyCount: usageData.used.monthly || 0
                                                    })}
                                                    className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    Restore
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4">
                                        <button
                                            type="button"
                                            onClick={setUsageCounts}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <Edit3 size={16} className="mr-2"/>
                                            Update Usage Counts
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Reset Tab */}
                            {activeTab === 'reset' && (
                                <div className="space-y-6">
                                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                                        <div className="flex">
                                            <ShieldAlert size={20} className="text-red-500 mr-3 flex-shrink-0"/>
                                            <div>
                                                <p className="text-red-700 font-medium">Reset Usage Counters</p>
                                                <p className="text-red-600 text-sm">
                                                    This action will reset the selected usage counters to zero. The
                                                    action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                            <h3 className="text-sm font-medium text-gray-700">Select Counters to
                                                Reset</h3>
                                        </div>
                                        <div className="p-4 space-y-4">
                                            <div className="flex items-center">
                                                <input
                                                    id="resetDaily"
                                                    type="checkbox"
                                                    checked={resetForm.resetDaily}
                                                    onChange={(e) => setResetForm({
                                                        ...resetForm,
                                                        resetDaily: e.target.checked
                                                    })}
                                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor="resetDaily"
                                                       className="ml-2 block text-sm text-gray-700">
                                                    Reset daily counter (Current: {usageData.used.daily || 0})
                                                </label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    id="resetMonthly"
                                                    type="checkbox"
                                                    checked={resetForm.resetMonthly}
                                                    onChange={(e) => setResetForm({
                                                        ...resetForm,
                                                        resetMonthly: e.target.checked
                                                    })}
                                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor="resetMonthly"
                                                       className="ml-2 block text-sm text-gray-700">
                                                    Reset monthly counter (Current: {usageData.used.monthly || 0})
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4">
                                        <button
                                            type="button"
                                            onClick={resetUsageCounters}
                                            disabled={!resetForm.resetDaily && !resetForm.resetMonthly}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <RotateCcw size={16} className="mr-2"/>
                                            Reset Selected Counters
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                    <div className="flex flex-col items-center">
                        <User size={40} className="text-gray-400 mb-4"/>
                        <h3 className="text-lg font-medium text-gray-800 mb-2">No user selected</h3>
                        <p className="text-gray-600 mb-4">
                            Search for a user by email to view and manage their usage limits
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

// Add some required CSS animations
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
  @keyframes fadeInOut {
    0% { opacity: 0; transform: translateY(-10px); }
    10% { opacity: 1; transform: translateY(0); }
    90% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(-10px); }
  }
  
  .animate-fadeInOut {
    animation: fadeInOut 3s ease-in-out;
  }
`;
document.head.appendChild(styleSheet);

export default AdminUsagePage;